import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, Eye } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { v: "all", l: "All" }, { v: "requested", l: "Requested" }, { v: "confirmed", l: "Confirmed" },
  { v: "waitlisted", l: "Waitlisted" }, { v: "attended", l: "Attended" }, { v: "cancelled", l: "Cancelled" },
];

export default function StaffBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = ["super_admin", "head_office_admin", "franchise_owner", "location_manager"].includes(user?.role);
  const [bookings, setBookings] = useState([]);

  const load = async () => { const r = await api.get("/bookings"); setBookings(r.data); };
  useEffect(() => { load(); }, []);

  const confirm = async (b) => { try { await api.post(`/bookings/${b.id}/confirm`); toast.success("Booking confirmed"); await load(); } catch { toast.error("Failed"); } };
  const cancel = async (b) => { const reason = window.prompt("Cancellation reason (optional):") ?? ""; try { await api.post(`/bookings/${b.id}/cancel`, { reason }); toast.success("Booking cancelled"); await load(); } catch { toast.error("Failed"); } };

  const filtered = (s) => s === "all" ? bookings : bookings.filter((b) => b.booking_status === s);

  return (
    <div>
      <PageHeader title="Bookings" subtitle="Manage enrollment requests and confirmations across your classes." />
      <Tabs defaultValue="all">
        <TabsList className="h-auto flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`bookings-tab-${t.v}`}>{t.l} <span className="ml-1.5 text-xs text-muted-foreground">({filtered(t.v).length})</span></TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.v} value={t.v} className="mt-4">
            <Card className="border-border overflow-hidden">
              <Table data-testid="bookings-table">
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    {["Dog", "Class", "Date", "Time", "Location", "Trainer", "Status", ""].map((h) => (
                      <TableHead key={h} className="text-xs uppercase tracking-wide font-semibold">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered(t.v).map((b) => (
                    <TableRow key={b.id} className="hover:bg-muted/40" data-testid="booking-row">
                      <TableCell className="font-medium">{b.dog_name}</TableCell>
                      <TableCell>{b.class_name || "—"}</TableCell>
                      <TableCell>{fmtDate(b.date)}</TableCell>
                      <TableCell>{b.time || "—"}</TableCell>
                      <TableCell>{b.location_name || "—"}</TableCell>
                      <TableCell>{b.trainer_name || "—"}</TableCell>
                      <TableCell><StatusBadge value={b.booking_status} /></TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {b.class_id && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/classes/${b.class_id}`)} data-testid="booking-open-class"><Eye size={15} /></Button>}
                          {canManage && b.booking_status === "requested" && <Button size="sm" className="h-8 gap-1" onClick={() => confirm(b)} data-testid="booking-confirm"><Check size={14} /> Confirm</Button>}
                          {canManage && !["cancelled", "attended", "no_show"].includes(b.booking_status) && <Button variant="outline" size="sm" className="h-8 gap-1 text-destructive" onClick={() => cancel(b)} data-testid="booking-cancel-staff"><X size={14} /> Cancel</Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered(t.v).length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No bookings here.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
